import * as moment from 'moment';
import { G37Response } from '../../services/api/coverage/coverage.service';
import { ActionsProductSpec } from '../../services/api/product/product.service';

export enum AddressCharRef {
  ac_home_id = 'ac_home_id',
}

export class Address {
  id: string;
  domain: string;
  addressSpecRef: string;
  describedByList: {
    value: string;
    addressCharRef: AddressCharRef;
  }[];

  constructor(id: number, homeId: string) {
    this.id = id.toString();
    this.domain = 'FTTH';
    this.addressSpecRef = 'HomeAdrSpec_v1.0.0';
    this.describedByList = [{
      value: homeId,
      addressCharRef: AddressCharRef.ac_home_id,
    }];
  }
}

export enum PlaceRole {
  SERVICE_LOCATION = 'SERVICE_LOCATION',
  INSTALLATION_LOCATION = 'INSTALLATION_LOCATION',
}

export class Place {
  id: string;
  domain: string;
  refId: string;
  roles: PlaceRole[];
  addressRefList: { relatedId: typeof Address.prototype.id; }[];

  constructor(id: number, address: Address, role: PlaceRole) {
    this.id = id.toString();
    this.domain = 'FTTH';
    this.refId = address.describedByList.filter(characteristic => characteristic.addressCharRef === AddressCharRef.ac_home_id)[0].value;
    this.roles = [role];
    this.addressRefList = [{ relatedId: address.id }];
  }
}

export enum ProductSpecId {
  pc_ftth_a_vlan_1 = 'pc_ftth_a_vlan_1',
  pc_ftth_a_vlan_2 = 'pc_ftth_a_vlan_2',
  pc_ftth_a_vlan_3 = 'pc_ftth_a_vlan_3',
  pc_ftth_a_vlan_4 = 'pc_ftth_a_vlan_4',
  pc_ftth_a_vlan_5 = 'pc_ftth_a_vlan_5',
  pc_ftth_c_vlan_1 = 'pc_ftth_c_vlan_1',
  pc_ftth_c_vlan_2 = 'pc_ftth_c_vlan_2',
  pc_ftth_c_vlan_3 = 'pc_ftth_c_vlan_3',
  pc_ftth_c_vlan_4 = 'pc_ftth_c_vlan_4',
  pc_ftth_c_vlan_5 = 'pc_ftth_c_vlan_5',
  pc_ftth_client_id = 'pc_ftth_client_id',
  pc_ftth_client_segment = 'pc_ftth_client_segment',
  pc_ftth_gescal = 'pc_ftth_gescal',
  pc_ftth_home_id = 'pc_ftth_home_id',
  pc_ftth_op_vlan = 'pc_ftth_op_vlan',
  pc_ftth_qos = 'pc_ftth_qos',
  pc_ftth_remote_id = 'pc_ftth_remote_id',
  pc_ftth_remote_id_tv = 'pc_ftth_remote_id_tv',
  pc_ftth_technology = 'pc_ftth_technology',
  pc_ftth_template = 'pc_ftth_template',
  pc_ftth_territory_owner = 'pc_ftth_territory_owner',
  pc_ftth_territory_user = 'pc_ftth_territory_user',
  pc_ftth_s_vlan = 'pc_ftth_s_vlan',
  pc_ftth_ppai = 'pc_ftth_ppai',
}

interface Vlan {
  vlan1: string;
  vlan2?: string;
  vlan3?: string;
  vlan4?: string;
  vlan5?: string;
}

export class Product {
  id: string;
  domainName: string;
  searchKey: string;
  template: string;
  project: string;
  specId: string;
  describedByList: {
    value: string;
    specId: ProductSpecId;
    externalId?: string;
  }[];

  constructor(
    id: number,
    g37Response: G37Response,
    productSpec: ActionsProductSpec,
    externalId: string,
    template: string,
    remoteId: string,
    ppai: string,
    isp: string,
  ) {
    this.id = id.toString();
    this.domainName = 'FTTH';
    this.searchKey = g37Response.ID_DOMICILIO_TO;
    this.specId = productSpec.productSpecNamingId;
    this.project = `ONIVIA-${externalId}-ALTA`;
    this.template = template;

    this.describedByList = []
    this.setSpec(ProductSpecId.pc_ftth_template, template);
    this.setSpec(ProductSpecId.pc_ftth_home_id, this.searchKey);
    this.setSpec(ProductSpecId.pc_ftth_territory_user, isp);
    this.setSpec(ProductSpecId.pc_ftth_gescal, g37Response.Gescal37);
    this.setSpec(ProductSpecId.pc_ftth_remote_id, remoteId);
    this.setSpec(ProductSpecId.pc_ftth_ppai, ppai);
  }

  set svlan(svlan: string) {
    this.setSpec(ProductSpecId.pc_ftth_s_vlan, svlan);
  }

  set avlan(vlan: Vlan) {
    const { vlan1, vlan2, vlan3, vlan4, vlan5 } = vlan;
    this.setSpec(ProductSpecId.pc_ftth_a_vlan_1, vlan1);

    if (vlan2) {
      this.setSpec(ProductSpecId.pc_ftth_a_vlan_2, vlan2);
    }
    if (vlan3) {
      this.setSpec(ProductSpecId.pc_ftth_a_vlan_3, vlan3);
    }
    if (vlan4) {
      this.setSpec(ProductSpecId.pc_ftth_a_vlan_4, vlan4);
    }
    if (vlan5) {
      this.setSpec(ProductSpecId.pc_ftth_a_vlan_5, vlan5);
    }
  }

  set cvlan(vlan: Vlan) {
    const { vlan1, vlan2, vlan3, vlan4, vlan5 } = vlan;
    this.setSpec(ProductSpecId.pc_ftth_c_vlan_1, vlan1);

    if (vlan2) {
      this.setSpec(ProductSpecId.pc_ftth_c_vlan_2, vlan2);
    }
    if (vlan3) {
      this.setSpec(ProductSpecId.pc_ftth_c_vlan_3, vlan3);
    }
    if (vlan4) {
      this.setSpec(ProductSpecId.pc_ftth_c_vlan_4, vlan4);
    }
    if (vlan5) {
      this.setSpec(ProductSpecId.pc_ftth_c_vlan_5, vlan5);
    }
  }

  set remoteIdTv(remoteIdTv: string) {
    this.setSpec(ProductSpecId.pc_ftth_remote_id_tv, remoteIdTv);
  }

  private setSpec(specId: ProductSpecId, value: string = '') {
    for (const describedByListElement of this.describedByList) {
      if (describedByListElement.specId === specId) {
        describedByListElement.value = value;
        return;
      }
    }

    this.describedByList.push({
      value,
      specId,
    });
  }
}

export class ProductOrder {
  domain: string;
  productOrderType: string;
  externalId: string;
  crm: string;
  action: string;
  orderDate: string;
  completionDate?: string;
  project: string;
  requestStartDate: string;
  requestCompletionDate?: string;
  expectedStartDate?: string;
  expectedCompletionDate?: string;
  execution: number;
  urgent: boolean;
  category: string;
  productRef: { relatedId: typeof Product.prototype.id; }[];
  placeRefList: { relatedId: typeof Place.prototype.id; }[];

  constructor(
    externalId: string,
    productSpec: ActionsProductSpec,
    now: moment.Moment,
    products: Product[],
    places: Place[],
  ) {
    const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
    this.domain = 'FTTH';
    this.productOrderType = productSpec.productSpecName.includes('Business') ? '2' : '1';
    this.externalId = externalId;
    this.project = `ONIVIA-${externalId}-ALTA`;
    this.crm = 'customer-portal';
    this.action = 'add';
    this.orderDate = now.format(dateFormat);
    this.requestStartDate = now.format(dateFormat);
    this.execution = 0;
    this.urgent = false;
    this.category = 'Product';
    this.productRef = products.map(product => ({ relatedId: product.id }));
    this.placeRefList = places.map(place => ({ relatedId: place.id }));
  }
}

export function generateExternalID() {
  return `ONI${moment().format('x')}`;
}

export class OrderContainer {
  addresses: Address[];
  places: Place[];
  products: Product[];
  productOrder: ProductOrder;

  constructor(
    g37: G37Response,
    productSpec: ActionsProductSpec,
    template: string,
    remoteId: string,
    ppai: string,
    isp: string,
    externalId: string,
  ) {
    const now = moment();

    const address = new Address(1, g37.ID_DOMICILIO_TO);
    this.addresses = [address];

    const place = new Place(1, address, PlaceRole.SERVICE_LOCATION);
    this.places = [place];

    const product = new Product(1, g37, productSpec, externalId, template, remoteId, ppai, isp);
    this.products = [product];

    this.productOrder = new ProductOrder(externalId, productSpec, now, this.products, this.places);
  }

  set svlan(svlan: string) {
    this.products[0].svlan = svlan;
  }

  set avlan(vlan: Vlan) {
    this.products[0].avlan = vlan;
  }

  set cvlan(vlan: Vlan) {
    this.products[0].cvlan = vlan
  }

  set remoteIdTv(remoteIdTv: string) {
    this.products[0].remoteIdTv = remoteIdTv;
  }
}
